/**
 * Saved progress for the step-by-step club setup (/setup/build) — KAN-118 AC6 / AC7.
 *
 * With both onboarding pop-ups retired, GuidedSetup is how a new owner builds their club, and it
 * kept everything in React state: a reload or a new tab started over at the club step, and the club
 * was POSTed again. This keeps the owner's place in localStorage (so a new tab sees it too).
 *
 * WHAT IS SAVED, AND WHAT NEVER IS. Only the step, the created club's id, the club details, the team
 * entries and the coach-invite entries — adult and club data — plus counts. Children's details from
 * the players step (names, birthdays, the parent's name and email) are NEVER written to browser
 * storage (Law 4): the record type below has no field that could hold them, and `saveProgress`
 * copies field by field rather than spreading whatever it is handed, so a PlayerDraft cannot ride
 * along by accident.
 *
 * WHOSE IT IS. One key per user per club, so a shared browser never hands one owner's progress to
 * another, or to the same owner in a different club. Cleared when setup finishes, and on sign-out
 * (AuthContext calls clearAllGuidedSetupProgress when the session ends).
 *
 * Storage can be unavailable (private mode, quota): every access is guarded, and a failure only
 * means the page does not resume — never an error in the owner's way.
 */

import {
  SETUP_STEPS,
  type SetupStep,
  type ClubDraft,
  type TeamDraft,
  type CoachInviteDraft,
} from '../pages/guided-setup-rules';

const PREFIX = 'kantera_guided_setup:';
const VERSION = 'v1';

/** A team entry, and whether it has already been created (so a retry never creates it twice). */
export interface SavedTeam extends TeamDraft {
  created: boolean;
}

/** A coach-invite entry, and whether it has already been sent. */
export interface SavedInvite extends CoachInviteDraft {
  sent: boolean;
}

export interface CreatedCounts {
  teams: number;
  players: number;
  invites: number;
  pendingConsent: number;
}

export interface GuidedSetupProgress {
  step: SetupStep;
  clubId: string | null;
  club: ClubDraft;
  teams: SavedTeam[];
  coaches: SavedInvite[];
  created: CreatedCounts;
}

export function progressKey(userId: string, orgId: string): string {
  return `${PREFIX}${VERSION}:${userId}:${orgId}`;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const count = (v: unknown): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0;

function readClub(v: unknown): ClubDraft | null {
  if (!isObj(v)) return null;
  const name = str(v.name);
  const city = str(v.city);
  const state = str(v.state);
  const timezone = str(v.timezone);
  if (name === null || city === null || state === null || timezone === null) return null;
  return { name, city, state, timezone };
}

function readTeam(v: unknown): SavedTeam | null {
  if (!isObj(v)) return null;
  const name = str(v.name);
  const division = str(v.division);
  const ageGroup = str(v.ageGroup);
  const maxPlayers = v.maxPlayers;
  if (name === null || division === null || ageGroup === null) return null;
  if (typeof maxPlayers !== 'number' || !Number.isFinite(maxPlayers)) return null;
  return { name, division, ageGroup, maxPlayers, created: v.created === true };
}

function readInvite(v: unknown): SavedInvite | null {
  if (!isObj(v)) return null;
  const email = str(v.email);
  const role = str(v.role);
  if (email === null || role === null) return null;
  return { email, role, sent: v.sent === true };
}

function readList<T>(v: unknown, read: (x: unknown) => T | null): T[] | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const out: T[] = [];
  for (const item of v) {
    const parsed = read(item);
    if (!parsed) return null;
    out.push(parsed);
  }
  return out;
}

/** Parse a stored record, or null if it is anything other than exactly what saveProgress writes. */
function parse(raw: string): GuidedSetupProgress | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObj(data)) return null;
  const step = SETUP_STEPS.find((s) => s === data.step);
  const club = readClub(data.club);
  const teams = readList(data.teams, readTeam);
  const coaches = readList(data.coaches, readInvite);
  const clubId = str(data.clubId);
  if (!step || !club || !teams || !coaches) return null;
  // Past the club step there must be a club: resuming without one would create it again.
  if (step !== 'club' && !clubId) return null;
  const created = isObj(data.created) ? data.created : {};
  return {
    step,
    clubId,
    club,
    teams,
    coaches,
    created: {
      teams: count(created.teams),
      players: count(created.players),
      invites: count(created.invites),
      pendingConsent: count(created.pendingConsent),
    },
  };
}

export function loadProgress(userId: string, orgId: string): GuidedSetupProgress | null {
  const key = progressKey(userId, orgId);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = parse(raw);
    if (!parsed) window.localStorage.removeItem(key);
    return parsed;
  } catch {
    return null;
  }
}

export function saveProgress(userId: string, orgId: string, p: GuidedSetupProgress): void {
  // Field by field, never a spread: only what is listed here can reach browser storage.
  const record: GuidedSetupProgress = {
    step: p.step,
    clubId: p.clubId,
    club: { name: p.club.name, city: p.club.city, state: p.club.state, timezone: p.club.timezone },
    teams: p.teams.map((t) => ({
      name: t.name,
      division: t.division,
      ageGroup: t.ageGroup,
      maxPlayers: t.maxPlayers,
      created: t.created,
    })),
    coaches: p.coaches.map((c) => ({ email: c.email, role: c.role, sent: c.sent })),
    created: {
      teams: p.created.teams,
      players: p.created.players,
      invites: p.created.invites,
      pendingConsent: p.created.pendingConsent,
    },
  };
  try {
    window.localStorage.setItem(progressKey(userId, orgId), JSON.stringify(record));
  } catch {
    // Not saved: the page just will not resume. Nothing to tell the owner mid-setup.
  }
}

export function clearProgress(userId: string, orgId: string): void {
  try {
    window.localStorage.removeItem(progressKey(userId, orgId));
  } catch {
    // Nothing stored that we can reach.
  }
}

/** Every user's saved setup progress on this browser — called when a session ends. */
export function clearAllGuidedSetupProgress(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) window.localStorage.removeItem(k);
  } catch {
    // Nothing stored that we can reach.
  }
}
